import React, { useState, useEffect, useCallback } from 'react';
import { callApi } from './api';
import ActivityLog from './ActivityLog'; // Import ActivityLog

// This form now handles creating, editing, and deleting a location.
function LocationForm({ ownerId, onFinished, initialData = null }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isEditMode = Boolean(initialData);

  useEffect(() => {
    if (isEditMode) {
      setName(initialData.name || '');
      setAddress(initialData.address || '');
    }
  }, [initialData, isEditMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      if (isEditMode) {
        await callApi('updateLocationV2', { locationId: initialData.id, data: { name, address } });
      } else {
        await callApi('createLocationV2', { ownerId, name, address });
      }
      if (onFinished) onFinished();
    } catch (err) {
      setError(`Kunne ikke ${isEditMode ? 'oppdatere' : 'opprette'} lokasjon: ` + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Er du sikker på at du vil slette lokasjonen "${initialData.name}"?`)) {
        setLoading(true);
        setError(null);
        try {
            await callApi('deleteLocationV2', { locationId: initialData.id });
            if (onFinished) onFinished();
        } catch (err) {
            setError('Kunne ikke slette lokasjon: ' + err.message);
        } finally {
            setLoading(false);
        }
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <form onSubmit={handleSubmit} style={{ background: 'var(--background-light)', color: 'var(--text-light)', padding: '20px', borderRadius: '5px', display: 'flex', flexDirection: 'column', gap: '15px', minWidth: '350px', maxWidth: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
        <h4>{isEditMode ? 'Rediger Lokasjon' : 'Opprett Ny Lokasjon'}</h4>
        <div>
          <label htmlFor="location-name" style={{ display: 'block', marginBottom: '5px' }}>Navn på lokasjon (f.eks. IKEA Slependen)</label>
          <input id="location-name" value={name} onChange={e => setName(e.target.value)} required />
        </div>
        <div>
          <label htmlFor="location-address" style={{ display: 'block', marginBottom: '5px' }}>Adresse (valgfritt)</label>
          <input id="location-address" value={address} onChange={e => setAddress(e.target.value)} />
        </div>
        {error && <p style={{ color: 'red' }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
            {isEditMode && <button type="button" onClick={handleDelete} disabled={loading} style={{backgroundColor: '#ff4d4d'}}>Slett</button>}
            {!isEditMode && <div />} {/* Placeholder for alignment */}
            <div>
                <button type="button" onClick={onFinished} style={{marginRight: '10px'}}>Avbryt</button>
                <button type="submit" disabled={loading}>{loading ? 'Lagrer...' : (isEditMode ? 'Lagre' : 'Opprett')}</button>
            </div>
        </div>
      </form>
    </div>
  );
}

function LocationsDashboard({ owner, onDrillDown, onBack }) {
  const [view, setView] = useState('locations'); // 'locations' or 'logs'
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);

  const fetchLocations = useCallback(async () => {
    if (!owner?.id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await callApi('listLocationsByOwnerV2', { ownerId: owner.id });
      setLocations(result.locations);
    } catch (err) {
      setError('Kunne ikke hente lokasjoner. ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [owner.id]);

  useEffect(() => {
    if (view === 'locations') {
        fetchLocations();
    }
  }, [fetchLocations, view]);

  const handleFormFinished = () => {
    setShowCreateForm(false);
    setEditingLocation(null);
    fetchLocations();
  };
  
  const activeStyle = { backgroundColor: '#ccc', color: '#000', borderColor: '#000' };

  return (
    <div>
      <button onClick={onBack}>&larr; Tilbake til Eiere</button>
      <h2>Lokasjoner for {owner.name}</h2>
      
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <button onClick={() => setView('locations')} style={view === 'locations' ? activeStyle : {}}>Lokasjoner</button>
        <button onClick={() => setView('logs')} style={view === 'logs' ? activeStyle : {}}>Aktivitetslogg</button>
      </div>

      {view === 'locations' && (
        <div>
          <p>Her kan du administrere alle lokasjoner som tilhører den valgte eieren.</p>
          {!showCreateForm && !editingLocation && (
            <button onClick={() => setShowCreateForm(true)} style={{ margin: '20px 0' }}>
              + Opprett Ny Lokasjon
            </button>
          )}
          {(showCreateForm || editingLocation) && (
            <LocationForm
              ownerId={owner.id}
              onFinished={handleFormFinished}
              initialData={editingLocation}
            />
          )}
          <h3>Eksisterende Lokasjoner</h3>
          {loading && <p>Laster lokasjoner...</p>}
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {!loading && !error && (
            <table className="user-table">
              <thead>
                <tr>
                  <th>Navn</th>
                  <th>Adresse</th>
                  <th style={{width: '1%', whiteSpace: 'nowrap'}}>Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {locations.map(loc => (
                  <tr key={loc.id}>
                    <td>{loc.name}</td>
                    <td>{loc.address || 'N/A'}</td>
                    <td style={{width: '1%', whiteSpace: 'nowrap'}}>
                      <button onClick={() => setEditingLocation(loc)}>Rediger</button>
                      <button onClick={() => onDrillDown(loc, 'location')}>Administrer Porter</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {view === 'logs' && (
        <ActivityLog ownerId={owner.id} />
      )}
    </div>
  );
}

export default LocationsDashboard;
